import { useState } from 'react'

import type { Board } from '../../api/boards'
import { Button } from '../../ui/Control'

/**
 * The boards themselves: made, named, copied, cleared, deleted.
 *
 * A screen of its own at `#/desk/boards`, rather than a menu over the surface, because these are the
 * things somebody does *between* pieces of work — at the end of a semester, when a new subject
 * starts — and none of them belongs in the way while a board is being arranged.
 *
 * **Clearing is deliberately not here.** It deletes, and it deletes what somebody arranged — so it
 * belongs on the board being looked at, where the arrangement is, and not in a list where it would
 * be a decision about something out of sight.
 *
 * **Duplicating is what a snapshot is.** There is no marked, read-only kind of board: a copy is an
 * ordinary board from the moment it exists. Its sticky notes are its own and go their own way; its
 * cards are still references, so it shows today's library at the old places — which is exactly what
 * somebody wants from a copy kept at the end of a semester.
 */
export interface BoardsProps {
  readonly boards: readonly Board[]
  readonly open: string | null
  readonly onOpen: (board: Board) => void
  readonly onCreate: (name: string) => void
  readonly onRename: (board: Board, name: string) => void
  readonly onMakeDefault: (board: Board) => void
  readonly onDuplicate: (board: Board, name: string) => void
  readonly onDelete: (board: Board) => void
  /** How much would go if this board were cleared — what the question is built from. */
  readonly countOn: (board: Board) => number | null
}

export function Boards({
  boards,
  open,
  onOpen,
  onCreate,
  onRename,
  onMakeDefault,
  onDuplicate,
  onDelete,
  countOn,
}: BoardsProps) {
  const [making, setMaking] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)

  return (
    <div className="boards">
      <header className="boards__head">
        <h2 className="boards__title">Boards</h2>
        <Button
          tone="primary"
          onClick={() => {
            setMaking(true)
          }}
        >
          New board
        </Button>
      </header>

      <ul className="boards__list">
        {making && (
          <li className="boards__board boards__board--naming">
            <Naming
              initial=""
              label="Name of the new board"
              onDone={(name) => {
                setMaking(false)
                onCreate(name)
              }}
              onGiveUp={() => {
                setMaking(false)
              }}
            />
          </li>
        )}

        {boards.map((board) => (
          <li
            key={board.id}
            className={board.id === open ? 'boards__board boards__board--open' : 'boards__board'}
          >
            {renaming === board.id ? (
              <Naming
                initial={board.name}
                label={`New name for ${board.name}`}
                onDone={(name) => {
                  setRenaming(null)
                  onRename(board, name)
                }}
                onGiveUp={() => {
                  setRenaming(null)
                }}
              />
            ) : (
              <button
                type="button"
                className="boards__open"
                aria-current={board.id === open ? 'true' : undefined}
                onClick={() => {
                  onOpen(board)
                }}
              >
                <span className="boards__name">{board.name}</span>
                {board.isDefault && <span className="boards__mark">Where you land</span>}
              </button>
            )}

            <div className="boards__doing">
              <Button
                tone="bare"
                onClick={() => {
                  setRenaming(board.id)
                }}
              >
                Rename
              </Button>
              {!board.isDefault && (
                <Button
                  tone="bare"
                  onClick={() => {
                    onMakeDefault(board)
                  }}
                >
                  Land here
                </Button>
              )}
              <Button
                tone="bare"
                onClick={() => {
                  onDuplicate(board, `${board.name} (copy)`)
                }}
              >
                Duplicate
              </Button>
              {boards.length > 1 && (
                <Button
                  tone="bare"
                  className="control--danger"
                  onClick={() => {
                    onDelete(board)
                  }}
                >
                  Delete
                </Button>
              )}
            </div>

            <p className="boards__facts">{factsAbout(board, countOn(board))}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function factsAbout(board: Board, count: number | null): string {
  const made = new Date(board.createdAt)
  const when = Number.isNaN(made.getTime())
    ? ''
    : made.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  if (count === null) {
    return when
  }
  return `${count === 1 ? '1 thing on it' : `${count} things on it`}${when === '' ? '' : ` · ${when}`}`
}

/**
 * A board being named, in the place the board will be.
 *
 * Deliberately not a form — no label, no Done, no Cancel — exactly as a folder is named in the
 * library: the name is typed where the name will stand, Enter keeps it, Escape drops it.
 */
function Naming({
  initial,
  label,
  onDone,
  onGiveUp,
}: {
  readonly initial: string
  readonly label: string
  readonly onDone: (name: string) => void
  readonly onGiveUp: () => void
}) {
  const [name, setName] = useState(initial)
  // Escape unmounts the field, which blurs it — and a blur keeps what was typed. Without this,
  // giving up would create the board anyway.
  const [settled, setSettled] = useState(false)

  const keep = () => {
    if (settled) {
      return
    }
    setSettled(true)
    const wanted = name.trim()
    if (wanted === '') {
      onGiveUp()
    } else {
      onDone(wanted)
    }
  }

  return (
    <input
      className="boards__field"
      autoFocus
      aria-label={label}
      placeholder="Board name"
      value={name}
      onChange={(event) => {
        setName(event.target.value)
      }}
      onBlur={keep}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          keep()
        }
        if (event.key === 'Escape') {
          setSettled(true)
          onGiveUp()
        }
      }}
    />
  )
}
